import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { collection, writeBatch, doc } from 'firebase/firestore';
import { ArrowLeft, Upload, FileText, CheckCircle, AlertTriangle, X, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Registration } from '../types';

interface ValidationError {
  index: number;
  field: string;
  message: string;
  data: any;
}

interface ImportSummary {
  totalRecords: number;
  validRecords: number;
  invalidRecords: number;
  validRegistrations: Registration[];
  errors: ValidationError[];
}

export default function ImportRegistrations() {
  const { signOut } = useAuth();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importSummary, setImportSummary] = useState<ImportSummary | null>(null);
  const [importing, setImporting] = useState(false);
  const [importComplete, setImportComplete] = useState(false);
  const [importResults, setImportResults] = useState<{ success: number; errors: string[] } | null>(null);

  const validateRegistration = (data: any, index: number): { isValid: boolean; errors: ValidationError[]; registration?: Registration } => {
    const errors: ValidationError[] = [];

    // Check required fields
    const requiredFields = ['userId', 'licensePlate', 'countryCode', 'busCompany', 'createdAt', 'location', 'timestamp'];
    
    for (const field of requiredFields) {
      if (!data[field]) {
        errors.push({
          index,
          field,
          message: `Missing required field: ${field}`,
          data
        });
      }
    }

    // Validate data types and structure
    if (data.userId && typeof data.userId !== 'string') {
      errors.push({
        index,
        field: 'userId',
        message: 'userId must be a string',
        data
      });
    }

    if (data.licensePlate && typeof data.licensePlate !== 'string') {
      errors.push({
        index,
        field: 'licensePlate',
        message: 'licensePlate must be a string',
        data
      });
    }

    if (data.countryCode && typeof data.countryCode !== 'string') {
      errors.push({
        index,
        field: 'countryCode',
        message: 'countryCode must be a string',
        data
      });
    }

    if (data.busCompany && typeof data.busCompany !== 'string') {
      errors.push({
        index,
        field: 'busCompany',
        message: 'busCompany must be a string',
        data
      });
    }

    // Validate timestamp structure
    if (data.timestamp) {
      // Handle both Firestore timestamp format and ISO string format
      if (typeof data.timestamp === 'string') {
        // Try to parse ISO string
        const parsedDate = new Date(data.timestamp);
        if (isNaN(parsedDate.getTime())) {
          errors.push({
            index,
            field: 'timestamp',
            message: 'timestamp string is not a valid ISO date',
            data
          });
        }
      } else if (typeof data.timestamp === 'object') {
        // Validate Firestore timestamp format
        if (typeof data.timestamp.seconds !== 'number' || 
            typeof data.timestamp.nanoseconds !== 'number') {
          errors.push({
            index,
            field: 'timestamp',
            message: 'timestamp object must have seconds and nanoseconds numbers',
            data
          });
        }
      } else {
        errors.push({
          index,
          field: 'timestamp',
          message: 'timestamp must be either an ISO date string or Firestore timestamp object',
          data
        });
      }
    }

    // Validate createdAt structure
    if (data.createdAt) {
      // Handle both Firestore timestamp format and ISO string format
      if (typeof data.createdAt === 'string') {
        // Try to parse ISO string
        const parsedDate = new Date(data.createdAt);
        if (isNaN(parsedDate.getTime())) {
          errors.push({
            index,
            field: 'createdAt',
            message: 'createdAt string is not a valid ISO date',
            data
          });
        }
      } else if (typeof data.createdAt === 'object') {
        // Validate Firestore timestamp format
        if (typeof data.createdAt.seconds !== 'number' || 
            typeof data.createdAt.nanoseconds !== 'number') {
          errors.push({
            index,
            field: 'createdAt',
            message: 'createdAt object must have seconds and nanoseconds numbers',
            data
          });
        }
      } else {
        errors.push({
          index,
          field: 'createdAt',
          message: 'createdAt must be either an ISO date string or Firestore timestamp object',
          data
        });
      }
    }

    // Validate location structure
    if (data.location) {
      if (typeof data.location !== 'object' ||
          typeof data.location.latitude !== 'number' ||
          typeof data.location.longitude !== 'number') {
        errors.push({
          index,
          field: 'location',
          message: 'location must be an object with latitude and longitude numbers',
          data
        });
      }
    }

    // Validate imageUrls if present
    if (data.imageUrls && !Array.isArray(data.imageUrls)) {
      errors.push({
        index,
        field: 'imageUrls',
        message: 'imageUrls must be an array',
        data
      });
    }

    // Validate merknad if present
    if (data.merknad && typeof data.merknad !== 'string') {
      errors.push({
        index,
        field: 'merknad',
        message: 'merknad must be a string',
        data
      });
    }

    const isValid = errors.length === 0;
    
    if (isValid) {
      // Convert ISO date strings to Firestore timestamp format if needed
      let timestamp = data.timestamp;
      let createdAt = data.createdAt;
      
      if (typeof data.timestamp === 'string') {
        const date = new Date(data.timestamp);
        timestamp = {
          seconds: Math.floor(date.getTime() / 1000),
          nanoseconds: (date.getTime() % 1000) * 1000000
        };
      }
      
      if (typeof data.createdAt === 'string') {
        const date = new Date(data.createdAt);
        createdAt = {
          seconds: Math.floor(date.getTime() / 1000),
          nanoseconds: (date.getTime() % 1000) * 1000000
        };
      }
      
      const registration: Registration = {
        userId: data.userId,
        licensePlate: data.licensePlate,
        imageUrls: data.imageUrls || [],
        countryCode: data.countryCode,
        busCompany: data.busCompany,
        createdAt: createdAt,
        location: data.location,
        merknad: data.merknad || '',
        timestamp: timestamp
      };
      
      return { isValid: true, errors: [], registration };
    }

    return { isValid: false, errors };
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSelectedFile(file);
    setImportSummary(null);
    setImportComplete(false);
    setImportResults(null);

    try {
      const text = await file.text();
      const jsonData = JSON.parse(text);

      if (!Array.isArray(jsonData)) {
        alert('JSON file must contain an array of registration objects');
        return;
      }

      const validRegistrations: Registration[] = [];
      const allErrors: ValidationError[] = [];

      jsonData.forEach((item, index) => {
        const { isValid, errors, registration } = validateRegistration(item, index);
        
        if (isValid && registration) {
          validRegistrations.push(registration);
        } else {
          allErrors.push(...errors);
        }
      });

      setImportSummary({
        totalRecords: jsonData.length,
        validRecords: validRegistrations.length,
        invalidRecords: jsonData.length - validRegistrations.length,
        validRegistrations,
        errors: allErrors
      });

    } catch (error) {
      if (error instanceof SyntaxError) {
        alert('Invalid JSON file. Please check the file format.');
      } else {
        alert('Error reading file: ' + (error as Error).message);
      }
    }
  };

  const executeImport = async () => {
    if (!importSummary || importSummary.validRecords === 0) return;

    setImporting(true);
    
    try {
      const batch = writeBatch(db);
      let successCount = 0;
      const errors: string[] = [];

      // Process in batches of 500 (Firestore limit)
      const batchSize = 500;
      const registrations = importSummary.validRegistrations;

      for (let i = 0; i < registrations.length; i += batchSize) {
        const currentBatch = writeBatch(db);
        const batchRegistrations = registrations.slice(i, i + batchSize);

        batchRegistrations.forEach((registration) => {
          const docRef = doc(collection(db, 'registrations'));
          currentBatch.set(docRef, registration);
        });

        try {
          await currentBatch.commit();
          successCount += batchRegistrations.length;
        } catch (error) {
          errors.push(`Batch ${Math.floor(i / batchSize) + 1}: ${(error as Error).message}`);
        }
      }

      setImportResults({
        success: successCount,
        errors
      });

      setImportComplete(true);
    } catch (error) {
      alert('Error during import: ' + (error as Error).message);
    } finally {
      setImporting(false);
    }
  };

  const downloadErrorReport = () => {
    if (!importSummary || importSummary.errors.length === 0) return;

    const csvContent = [
      'Record Index,Field,Error Message,Data',
      ...importSummary.errors.map(error => 
        `${error.index + 1},"${error.field}","${error.message}","${JSON.stringify(error.data).replace(/"/g, '""')}"`
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `import_errors_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const resetImport = () => {
    setSelectedFile(null);
    setImportSummary(null);
    setImportComplete(false);
    setImportResults(null);
    // Reset file input
    const fileInput = document.getElementById('file-input') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <div className="flex items-center">
              <Link
                to="/manage-registrations"
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors duration-150"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Tilbake til registreringer
              </Link>
            </div>
            <h1 className="text-xl font-semibold text-gray-900">Importer registreringer</h1>
            <button
              onClick={() => signOut()}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              Logg ut
            </button>
          </div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {importComplete && importResults ? (
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <CheckCircle className="h-8 w-8 text-green-500 mr-3" />
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">Import fullført</h2>
                    <p className="text-gray-600">
                      {importResults.success} registreringer ble importert
                      {importResults.errors.length > 0 && ` (${importResults.errors.length} feil)`}
                    </p>
                  </div>
                </div>
                <button
                  onClick={resetImport}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  Importer flere
                </button>
              </div>

              {importResults.errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-red-900 mb-2">Feil under import</h3>
                  <ul className="text-sm text-red-700 space-y-1">
                    {importResults.errors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="bg-white shadow rounded-lg p-6 mb-6">
                <h2 className="text-lg font-medium text-gray-900 mb-4">Last opp JSON-fil</h2>
                <p className="text-gray-600 mb-6">
                  Velg en JSON-fil som inneholder en array av registreringsobjekter. 
                  Filen vil bli validert før import.
                </p>
                
                <div className="flex items-center justify-center w-full">
                  <label htmlFor="file-input" className="flex flex-col items-center justify-center w-full h-64 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <Upload className="w-10 h-10 mb-3 text-gray-400" />
                      <p className="mb-2 text-sm text-gray-500">
                        <span className="font-semibold">Klikk for å laste opp</span> eller dra og slipp
                      </p>
                      <p className="text-xs text-gray-500">JSON filer (.json)</p>
                      {selectedFile && (
                        <p className="mt-2 text-sm text-blue-600 font-medium">
                          Valgt: {selectedFile.name}
                        </p>
                      )}
                    </div>
                    <input
                      id="file-input"
                      type="file"
                      accept=".json"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>

              {importSummary && (
                <div className="bg-white shadow rounded-lg p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center">
                      <FileText className="h-6 w-6 text-blue-600 mr-2" />
                      <h3 className="text-lg font-medium text-gray-900">Import-sammendrag</h3>
                    </div>
                    <button
                      onClick={resetImport}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <X className="h-4 w-4 mr-2" />
                      Avbryt
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-blue-900">{importSummary.totalRecords}</div>
                      <div className="text-sm text-blue-700">Totalt antall poster</div>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-green-900">{importSummary.validRecords}</div>
                      <div className="text-sm text-green-700">Gyldige registreringer</div>
                    </div>
                    <div className="bg-red-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-red-900">{importSummary.invalidRecords}</div>
                      <div className="text-sm text-red-700">Ugyldige poster</div>
                    </div>
                  </div>

                  {importSummary.validRecords > 0 && (
                    <div className="mb-6">
                      <h4 className="text-md font-medium text-gray-900 mb-3">Forhåndsvisning av gyldige registreringer</h4>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Reg.nr
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Land
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Selskap
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Dato
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {importSummary.validRegistrations.slice(0, 500).map((reg, index) => (
                              <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {reg.licensePlate}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {reg.countryCode}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {reg.busCompany}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {new Date(reg.timestamp.seconds * 1000).toLocaleDateString('no-NO')}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {importSummary.validRegistrations.length > 500 && (
                          <p className="text-sm text-gray-500 mt-2 px-6">
                            ... og {importSummary.validRegistrations.length - 500} flere registreringer
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {importSummary.errors.length > 0 && (
                    <div className="mb-6">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-md font-medium text-red-900">Valideringsfeil</h4>
                        <button
                          onClick={downloadErrorReport}
                          className="inline-flex items-center px-3 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-red-50 hover:bg-red-100"
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Last ned feilrapport
                        </button>
                      </div>
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-h-64 overflow-y-auto">
                        {importSummary.errors.slice(0, 10).map((error, index) => (
                          <div key={index} className="text-sm text-red-700 mb-2">
                            <strong>Post {error.index + 1}:</strong> {error.message} (felt: {error.field})
                          </div>
                        ))}
                        {importSummary.errors.length > 10 && (
                          <p className="text-sm text-red-600 font-medium">
                            ... og {importSummary.errors.length - 10} flere feil. Last ned feilrapporten for alle detaljer.
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {importSummary.validRecords > 0 && (
                    <div className="flex justify-end space-x-3">
                      <button
                        onClick={executeImport}
                        disabled={importing}
                        className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-400"
                      >
                        {importing ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Importerer...
                          </>
                        ) : (
                          <>
                            <CheckCircle className="h-5 w-5 mr-2" />
                            Bekreft import ({importSummary.validRecords} registreringer)
                          </>
                        )}
                      </button>
                    </div>
                  )}

                  {importSummary.validRecords === 0 && (
                    <div className="text-center py-8">
                      <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">Ingen gyldige registreringer funnet</h3>
                      <p className="text-gray-600">
                        Alle poster i filen har valideringsfeil. Last ned feilrapporten for å se detaljene.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}